import { motion } from "framer-motion";
import meta from "@/data/logoPieces.json";
import { useLang } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const EXIT_EASE = [0.76, 0, 0.24, 1];

// Official SOLIDOR mark — exact crops of the supplied artwork.
// Original logo geometry is preserved.
// Animation timing remains unchanged.
const pieceVariants = {
  hidden: (p) => ({
    opacity: 0,
    y: p.anim === "scale" ? 0 : (p.dir || 1) * 16,
    scale: p.anim === "scale" ? 0.93 : 1,
    rotate: p.anim === "s" ? (p.dir || 1) * 1.5 : 0,
  }),

  show: (p) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    rotate: 0,
    transition: {
      delay: p.delay,
      duration: p.dur,
      ease: EASE,
    },
  }),
};

export const Intro = ({ onDone }) => {
  const { t } = useLang();

  return (
    <motion.div
      data-testid="intro-fullscreen-loader"
      className="fixed inset-0 z-[100] flex cursor-pointer flex-col items-center justify-center overflow-hidden bg-solidor"
      onClick={onDone}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onDone();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label="Enter SOLIDOR"
      exit={{
        y: "-100%",
        transition: {
          duration: 0.85,
          ease: EXIT_EASE,
        },
      }}
    >
      {/* Very subtle depth in the background */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 1.8,
          ease: "easeOut",
        }}
        style={{
          background:
            "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.03), transparent 46%)",
        }}
      />

      {/* SOLIDOR LOGO */}
      <motion.div
        className="relative"
        initial={{
          y: 8,
        }}
        animate={{
          y: 0,
        }}
        transition={{
          duration: 1.1,
          ease: EASE,
        }}
      >
        <div
          className="relative h-60 sm:h-80"
          style={{
            aspectRatio: `${meta.mark.w} / ${meta.mark.h}`,
          }}
        >
          {meta.pieces.map((p) => (
            <motion.span
              key={p.src}
              custom={p}
              variants={pieceVariants}
              initial="hidden"
              animate="show"
              className="absolute block"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.w}%`,
                height: `${p.h}%`,
                backgroundColor: "#FFFFFF",
                WebkitMaskImage: `url(${p.src})`,
                maskImage: `url(${p.src})`,
                WebkitMaskSize: "100% 100%",
                maskSize: "100% 100%",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* ARCHITECTURE & DESIGN */}
      <div className="mt-12 overflow-hidden px-6">
        <motion.p
          data-testid="intro-tagline"
          className="text-center font-mono text-[11px] sm:text-xs tracking-[0.45em] text-white/80"
          initial={{
            y: "120%",
            opacity: 0,
          }}
          animate={{
            y: 0,
            opacity: 1,
          }}
          transition={{
            delay: 2.75,
            duration: 0.8,
            ease: EASE,
          }}
        >
          {t.intro.tagline}
        </motion.p>
      </div>

      {/* CLICK TO ENTER */}
      <motion.div
        className="mt-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: 3.5,
          duration: 1,
          ease: "easeOut",
        }}
      >
        <motion.span
          data-testid="intro-click-to-enter"
          className="block font-mono text-[10px] uppercase tracking-[0.55em] text-white/50 transition-colors duration-500 hover:text-white"
          animate={{
            opacity: [0.5, 0.9, 0.5],
          }}
          transition={{
            delay: 4.5,
            duration: 2.8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {t.intro.enter}
        </motion.span>
      </motion.div>

      {/* Minimal bottom detail */}
      <motion.div
        aria-hidden
        className="absolute bottom-8 sm:bottom-10 left-1/2 h-7 w-px -translate-x-1/2 bg-white/20"
        initial={{
          scaleY: 0,
          opacity: 0,
        }}
        animate={{
          scaleY: 1,
          opacity: 1,
        }}
        transition={{
          delay: 3.5,
          duration: 1,
          ease: EASE,
        }}
        style={{
          transformOrigin: "top",
        }}
      />
    </motion.div>
  );
};
